import { refreshToken } from "api/auth";
import { AuthTokens } from "types/auth";
import { refreshTokenService } from "../services/refreshTokenService";

// This utility will be used to automatically refresh tokens before API calls
export class AuthInterceptor {
  private static instance: AuthInterceptor;
  private refreshPromise: Promise<AuthTokens | null> | null = null;

  static getInstance(): AuthInterceptor {
    if (!AuthInterceptor.instance) {
      AuthInterceptor.instance = new AuthInterceptor();
    }
    return AuthInterceptor.instance;
  }

  // Check if token needs refresh (5 minutes before expiry)
  private isTokenExpiringSoon(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - currentTime;
      return timeUntilExpiry < 300; // 5 minutes
    } catch {
      return true;
    }
  }

  // Check if token is expired (like IM Partner Dashboard)
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }

  // Get stored tokens from localStorage
  private getStoredTokens(): AuthTokens | null {
    if (typeof window === "undefined") return null;
    
    try {
      const access_token = localStorage.getItem("access_token");
      const refresh_token = localStorage.getItem("refresh_token");
      const user_id = localStorage.getItem("user_id");
      const is_new_user = localStorage.getItem("is_new_user") === "true";

      if (access_token && refresh_token && user_id) {
        return {
          access_token,
          refresh_token,
          user_id,
          is_new_user,
        };
      }
    } catch (error) {
      console.error("Error reading auth tokens from storage:", error);
    }
    
    return null;
  }

  // Store tokens in localStorage
  private storeTokens(tokens: AuthTokens): void {
    if (typeof window === "undefined") return;
    
    try {
      localStorage.setItem("access_token", tokens.access_token);
      localStorage.setItem("refresh_token", tokens.refresh_token);
      localStorage.setItem("user_id", tokens.user_id);
      if (tokens.is_new_user !== undefined) {
        localStorage.setItem("is_new_user", tokens.is_new_user.toString());
      }
    } catch (error) {
      console.error("Error storing auth tokens:", error);
    }
  }

  // Clear tokens from localStorage
  private clearTokens(): void {
    if (typeof window === "undefined") return;
    
    try {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("is_new_user");
    } catch (error) {
      console.error("Error clearing auth tokens:", error);
    }
  }

  // Refresh token if needed
  async ensureValidToken(): Promise<AuthTokens | null> {
    const storedTokens = this.getStoredTokens();
    
    if (!storedTokens) {
      return null;
    }

    // Check if we're in cooldown period to avoid unnecessary refresh attempts
    if (refreshTokenService.isInCooldown()) {
      return null;
    }

    // If token is not expiring soon, return current tokens
    if (!this.isTokenExpiringSoon(storedTokens.access_token)) {
      return storedTokens;
    }

    // Use RefreshTokenService to coordinate refresh attempts
    try {
      const result = await refreshTokenService.refreshTokens();
      
      if (result.success) {
        // Get updated tokens from storage
        const updatedTokens = this.getStoredTokens();
        return updatedTokens;
      } else {
        // Refresh failed, clear tokens
        this.clearTokens();
        return null;
      }
    } catch (error) {
      this.clearTokens();
      return null;
    }
  }

  // Get current valid token (will refresh if needed)
  async getValidToken(): Promise<string | null> {
    const tokens = await this.ensureValidToken();
    return tokens?.access_token || null;
  }
}

// Export singleton instance
export const authInterceptor = AuthInterceptor.getInstance();
